import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  UserRole
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useCreateOrder } from "../../../hooks/api/useSalesOrder";
import { useListStores } from "../../../hooks/api/useStore";
import { useGetCustomerList } from "../../../hooks/api/useCustomer";
import type { AddSalesOrderModalProps } from "../interfaces/modals";
import { useForm, Controller, SubmitHandler, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGetProfile } from "../../../hooks/api/useProfile";
import type { Customer } from "../../../Graphql/types/customer";
import QuickAddCustomerModal from "./QuickAddCustomerModal";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../../Utils/appLogger";

const customerFilter = createFilterOptions<Customer>({
  stringify: (option) =>
    [option.name, option.phone ?? "", option.email ?? ""].join(" ")
});

function customerOptionLabel(c: Customer): string {
  return c.name;
}

type FormValues = {
  storeId?: string;
  customerId: string;
};

const AddSalesOrderModal: React.FC<AddSalesOrderModalProps> = ({
  open,
  onClose,
  onOrderCreated
}) => {
  const { t } = useKivunovaTranslation();
  const createOrder = useCreateOrder();
  const listStores = useListStores();
  const getProfile = useGetProfile();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userRole = userDetails?.role ?? null;
  const userStoreId = userDetails?.storeId ?? null;
  const userCompanyId = userDetails?.companyId ?? null;

  /**
   * Store dropdown is for company admins only. Other roles must have `storeId` on their profile;
   * if it is missing, submit is disabled and we show an error (no silent empty payload).
   */
  const needsStorePicker = userRole === UserRole.COMPANY_ADMIN;
  const missingNonAdminStore =
    Boolean(userDetails) &&
    !needsStorePicker &&
    !String(userStoreId ?? "").trim();

  const storeOptions = useSelector((state: RootState) => state.store.storeList);

  const [submitError, setSubmitError] = useState("");
  const [fetchingStores, setFetchingStores] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const schema = useMemo(
    () =>
      yup.object({
        storeId: needsStorePicker
          ? yup.string().required("Please select a store")
          : yup.string().optional(),
        customerId: yup
          .string()
          .trim()
          .required(
            t("add-sales-order-customer-required", "Please select a customer")
          )
      }),
    [needsStorePicker, t]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    /* eslint-disable @typescript-eslint/no-explicit-any */
    resolver: yupResolver(schema) as any,
    defaultValues: {
      storeId: "",
      customerId: ""
    }
  });

  const watchedStoreId = useWatch({ control, name: "storeId" });

  const orderStoreScopeId = needsStorePicker
    ? String(watchedStoreId ?? "").trim()
    : String(userStoreId ?? "").trim();

  const prevOrderStoreScopeRef = useRef<string>("");

  const { data: customerList = [], isLoading: fetchingCustomers } =
    useGetCustomerList(userCompanyId ?? undefined, orderStoreScopeId || null);

  /** Until the customer list query includes the new row, keep the created customer as an Autocomplete option. */
  const [customerPickerExtra, setCustomerPickerExtra] =
    useState<Customer | null>(null);

  const customerOptions = useMemo(() => {
    if (
      !customerPickerExtra ||
      customerList.some((c) => c.id === customerPickerExtra.id)
    ) {
      return customerList;
    }
    return [...customerList, customerPickerExtra];
  }, [customerList, customerPickerExtra]);

  useEffect(() => {
    if (!open) return;

    reset({ storeId: "", customerId: "" });
    setSubmitError("");
    setCustomerPickerExtra(null);
    prevOrderStoreScopeRef.current = "";

    if (!userDetails) {
      getProfile.mutateAsync().catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !needsStorePicker) return;
    const sid = String(watchedStoreId ?? "").trim();
    const prev = prevOrderStoreScopeRef.current;
    if (prev && prev !== sid) {
      setValue("customerId", "");
      setCustomerPickerExtra(null);
    }
    prevOrderStoreScopeRef.current = sid;
  }, [open, needsStorePicker, watchedStoreId, setValue]);

  useEffect(() => {
    if (
      !customerPickerExtra ||
      !customerList.some((c) => c.id === customerPickerExtra.id)
    ) {
      return;
    }
    setCustomerPickerExtra(null);
  }, [customerList, customerPickerExtra]);

  useEffect(() => {
    if (!open || !needsStorePicker || !userCompanyId) return;

    setFetchingStores(true);
    listStores
      .mutateAsync({ companyId: userCompanyId })
      .catch(() => {})
      .finally(() => {
        setFetchingStores(false);
      });
  }, [open, needsStorePicker, userCompanyId]);

  const handleCreate: SubmitHandler<FormValues> = async (values) => {
    setSubmitError("");

    const resolvedStoreId = String(userStoreId ?? values.storeId ?? "").trim();
    if (!resolvedStoreId) {
      setSubmitError(
        t(
          "add-sales-order-missing-store",
          needsStorePicker
            ? "Please select a store."
            : "Your account has no store assigned. Contact your administrator."
        )
      );
      return;
    }

    sendAppLog(
      LOG_LEVEL.INFO,
      "Started creating order from modal",
      buildLogContext(
        "orders",
        "create-order-modal",
        userDetails?.id,
        userCompanyId ?? undefined,
        resolvedStoreId,
        {
          storeId: resolvedStoreId,
          customerId: values.customerId.trim()
        }
      )
    );

    try {
      const newOrder = await createOrder.mutateAsync({
        storeId: resolvedStoreId,
        customerId: values.customerId.trim()
      });

      if (newOrder?.orderId) {
        sendAppLog(
          LOG_LEVEL.INFO,
          "Order created successfully from modal",
          buildLogContext(
            "orders",
            "create-order-modal",
            userDetails?.id,
            userCompanyId ?? undefined,
            resolvedStoreId,
            { orderId: newOrder.orderId }
          )
        );
        onOrderCreated?.(newOrder.orderId);
        onClose();
      }
    } catch (error) {
      captureAppException(
        "Failed to create order from modal",
        error,
        buildLogContext(
          "orders",
          "create-order-modal",
          userDetails?.id,
          userCompanyId ?? undefined,
          resolvedStoreId,
          { customerId: values.customerId.trim() }
        )
      );
      setSubmitError(
        t("add-sales-order-error", "Failed to create order. Please try again.")
      );
    }
  };

  const handleClose = () => {
    reset({ storeId: "", customerId: "" });
    setSubmitError("");
    setQuickAddOpen(false);
    setCustomerPickerExtra(null);
    onClose();
  };

  const isLoadingProfile = !userDetails && getProfile.isPending;
  const isSubmitDisabled =
    createOrder.isPending ||
    isLoadingProfile ||
    missingNonAdminStore ||
    (needsStorePicker && fetchingStores);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modal-styles" data-testid="create-sales-order-modal">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("add-sales-order-title", "Create Sales Order")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          component="form"
          onSubmit={handleSubmit(handleCreate)}
          sx={{ p: matchMD ? 2 : 3 }}
        >
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t(
              "add-sales-order-description",
              "Choose a customer for this order. Use search to find them by name, phone, or email."
            )}
          </Typography>

          {isLoadingProfile && !needsStorePicker && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: matchMD ? 1 : 1.75,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                mb: 3
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {t("loading-profile", "Loading profile...")}
              </Typography>
            </Box>
          )}

          {missingNonAdminStore && (
            <Typography variant="body2" color="error" mb={2}>
              {t(
                "add-sales-order-no-profile-store",
                "Your account has no store assigned. You cannot create a sales order until an administrator assigns you to a store."
              )}
            </Typography>
          )}

          {/* Store picker — COMPANY_ADMIN only */}
          {needsStorePicker && (
            <Controller
              name="storeId"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  error={!!errors.storeId}
                  disabled={
                    createOrder.isPending || fetchingStores || isLoadingProfile
                  }
                  sx={{ mb: 2 }}
                >
                  <InputLabel id="store-select-label">
                    {t("add-sales-order-store-label", "Store")} *
                  </InputLabel>

                  {fetchingStores || isLoadingProfile ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: matchMD ? 1 : 1.75,
                        border: "1px solid",
                        borderColor: errors.storeId ? "error.main" : "divider",
                        borderRadius: 1,
                        mt: "1px"
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        {isLoadingProfile
                          ? t("loading-profile", "Loading profile...")
                          : t(
                              "add-sales-order-loading-stores",
                              "Loading stores..."
                            )}
                      </Typography>
                    </Box>
                  ) : (
                    <Select
                      {...field}
                      labelId="store-select-label"
                      label={`${t("add-sales-order-store-label", "Store")} *`}
                      data-testid="add-sales-order-store-select"
                    >
                      {storeOptions.length === 0 ? (
                        <MenuItem disabled value="">
                          {t(
                            "add-sales-order-no-stores",
                            "No stores available"
                          )}
                        </MenuItem>
                      ) : (
                        storeOptions.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  )}

                  {errors.storeId && (
                    <FormHelperText>{errors.storeId.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          )}

          {/* Customer picker — after store is known; company admin must pick store first */}
          {isLoadingProfile && !needsStorePicker ? null : needsStorePicker &&
            !String(watchedStoreId ?? "").trim() ? (
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t(
                "add-sales-order-select-store-for-customers",
                "Select a store to load customers for this order."
              )}
            </Typography>
          ) : fetchingCustomers ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: matchMD ? 1 : 1.75,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                mb: submitError ? 1 : 3
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {t("add-sales-order-loading-customers", "Loading customers...")}
              </Typography>
            </Box>
          ) : (
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => {
                const selected =
                  customerOptions.find((c) => c.id === field.value) ?? null;
                const storeChosen =
                  !needsStorePicker || !!String(watchedStoreId ?? "").trim();
                const pickerDisabled =
                  createOrder.isPending || isLoadingProfile || !storeChosen;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "flex-start",
                      mb: submitError ? 1 : 3
                    }}
                  >
                    <Autocomplete
                      fullWidth
                      size={matchMD ? "small" : "medium"}
                      options={customerOptions}
                      loading={fetchingCustomers}
                      value={selected}
                      onChange={(_, v) => field.onChange(v?.id ?? "")}
                      getOptionLabel={(c) => customerOptionLabel(c)}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      filterOptions={customerFilter}
                      disabled={pickerDisabled}
                      data-testid="add-sales-order-customer-autocomplete"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t(
                            "add-sales-order-customer-label",
                            "Customer"
                          )}
                          error={!!errors.customerId}
                          helperText={errors.customerId?.message}
                          inputProps={{
                            ...params.inputProps,
                            name: "customerId"
                          }}
                        />
                      )}
                      noOptionsText={t(
                        "add-sales-order-empty-customers",
                        "No customers found"
                      )}
                    />
                    <IconButton
                      color="primary"
                      onClick={() => setQuickAddOpen(true)}
                      disabled={pickerDisabled}
                      aria-label={t(
                        "add-sales-order-add-customer",
                        "Add new customer"
                      )}
                      sx={{ mt: 0.5 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                );
              }}
            />
          )}

          {quickAddOpen && userCompanyId ? (
            <QuickAddCustomerModal
              open={quickAddOpen}
              onClose={() => setQuickAddOpen(false)}
              companyId={userCompanyId}
              requireStoreId={needsStorePicker}
              storeIdForCreate={needsStorePicker ? watchedStoreId : userStoreId}
              onCustomerCreated={(customer) => {
                setValue("customerId", customer.id, { shouldValidate: true });
                setCustomerPickerExtra(customer);
                setQuickAddOpen(false);
              }}
            />
          ) : null}

          {/* Submit error */}
          {submitError && (
            <Typography variant="body2" color="error" mb={2}>
              {submitError}
            </Typography>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: matchMD ? "column" : "row",
              gap: matchMD ? 1 : 2
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={createOrder.isPending}
              disabled={isSubmitDisabled}
              fullWidth
              size={matchMD ? "medium" : "large"}
              data-testid="create-sales-order-submit"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("add-sales-order-submit-button", "Create Order")}
            </LoadingButton>

            <LoadingButton
              variant="outlined"
              onClick={handleClose}
              disabled={createOrder.isPending}
              fullWidth
              size={matchMD ? "medium" : "large"}
              data-testid="create-sales-order-cancel"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white",
                  borderColor: BRAND_HOVER
                }
              }}
            >
              {t("add-sales-order-cancel-button", "Cancel")}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddSalesOrderModal;
